import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import Navigation from "../../Navigation";
import Footer from "../../Footer";

const BlackDeathSimulationPage = () => {
  const { language } = useLanguage();
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef([]);

  // 初始化section refs
  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, 2); // 两个部分
  }, []);

  // 处理滚动
  useEffect(() => {
    const handleScroll = () => {
      // 根据滚动位置确定活动部分
      const currentPosition = window.pageYOffset + 300;
      let active = 0;

      sectionRefs.current.forEach((ref, index) => {
        if (ref && ref.offsetTop <= currentPosition) {
          active = index;
        }
      });

      if (active !== activeSection) {
        setActiveSection(active);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeSection]);

  // 翻译
  const translations = {
    backToProjects: {
      en: "Back to Projects",
      zh: "返回项目列表",
      ja: "プロジェクトに戻る",
    },
    title: {
      en: "Black Death Pandemic Simulation",
      zh: "黑死病大流行模拟",
      ja: "ペスト感染パンデミックシミュレーション",
    },
    tagline: {
      en: "SIR model-based visualization of the 14th century plague across medieval Europe",
      zh: "基于SIR模型的中世纪欧洲14世纪黑死病可视化展示",
      ja: "SIRモデルに基づく中世ヨーロッパの14世紀ペスト視覚化",
    },
    overview: {
      en: "Project Overview",
      zh: "项目概述",
      ja: "プロジェクト概要",
    },
    description: {
      en: "This project uses Matlab to create a visualization of the Black Death (1346-1353) based on the SIR epidemic model. It incorporates historical trade routes and demographics to illustrate how the plague spread across medieval Europe.",
      zh: "本项目使用Matlab基于SIR传染病模型创建黑死病(1346-1353)的可视化展示。模拟包含历史贸易路线和人口统计数据，展示瘟疫如何在中世纪欧洲传播。",
      ja: "このプロジェクトでは、MatlabとSIR疫学モデルを使用してペスト（1346-1353）の視覚化を作成しました。歴史的な交易ルートと人口統計を組み込み、中世ヨーロッパでペストがどのように広がったかを視覚的に表現します。",
    },
    methodology: {
      en: "Methodology",
      zh: "方法论",
      ja: "方法論",
    },
    methodologyText: {
      en: "We implemented the Susceptible-Infected-Recovered (SIR) model in Matlab to visualize disease spread patterns. The simulation parameters were calibrated using historical mortality rates and spread timelines, with trade routes serving as primary transmission vectors.",
      zh: "我们在Matlab中实现了易感-感染-恢复(SIR)模型来可视化疾病传播模式。模拟参数使用历史死亡率和传播时间线进行校准，贸易路线作为主要传播途径。",
      ja: "Matlabで感受性-感染-回復（SIR）モデルを実装し、疾病拡散パターンを視覚化しました。シミュレーションパラメータは歴史的な死亡率と拡散タイムラインを使用して調整され、交易ルートが主要な感染経路として機能しています。",
    },
    features: {
      en: "Key Features",
      zh: "主要特点",
      ja: "主な特徴",
    },
    results: {
      en: "Key Findings",
      zh: "主要发现",
      ja: "主な発見",
    },
    resultsText: {
      en: "Our SIR model visualization demonstrates mortality rates of 30-60% in affected regions, with R₀ values estimated between 2.5-3.8. Simulation reveals how urban centers acted as disease hubs while the impact diminished in isolated areas due to reduced contact rates.",
      zh: "我们的SIR模型可视化展示了受影响地区30-60%的死亡率，R₀值估计在2.5-3.8之间。模拟揭示了城市中心如何充当疾病枢纽，而在接触率降低的隔离地区，影响减弱。",
      ja: "私たちのSIRモデル視覚化では、影響を受けた地域の死亡率が30〜60％、R₀値は2.5〜3.8と推定されています。シミュレーションにより、都市部が感染拡大の中心として機能する一方、接触率の低い孤立地域では影響が軽減されることが明らかになりました。",
    },
    courseInfo: {
      en: "Course Information",
      zh: "课程信息",
      ja: "コース情報",
    },
    course: {
      en: "Course:",
      zh: "课程:",
      ja: "コース:",
    },
    instructor: {
      en: "Instructor:",
      zh: "教师:",
      ja: "教授:",
    },
  };

  // 技术栈数据
  const techStack = [
    { name: "Matlab", category: "language", color: "blue" },
    { name: "SIR Model", category: "concept", color: "purple" },
    { name: "Data Visualization", category: "feature", color: "teal" },
    { name: "Epidemiology", category: "concept", color: "purple" },
  ];

  // 按类别获取技术标签的颜色
  const getTagColor = (category) => {
    switch (category) {
      case "language":
        return "bg-blue-100 text-blue-800";
      case "library":
        return "bg-green-100 text-green-800";
      case "concept":
        return "bg-purple-100 text-purple-800";
      case "feature":
        return "bg-teal-100 text-teal-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-neutral-50">
      <Navigation activeSection="projects" />
      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="mb-6 flex justify-between items-center">
            <a
              href="/projects"
              className="text-primary-500 hover:text-primary-600 transition-colors flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              {translations.backToProjects[language] ||
                translations.backToProjects.en}
            </a>
          </div>

          <header className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-light mb-4 text-neutral-800">
              {translations.title[language] || translations.title.en}
            </h1>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              {translations.tagline[language] || translations.tagline.en}
            </p>

            {/* 技术栈标签 */}
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {techStack.map((tech, index) => (
                <span
                  key={index}
                  className={`${getTagColor(
                    tech.category
                  )} px-3 py-1 rounded-full text-sm font-medium`}
                >
                  {tech.name}
                </span>
              ))}
            </div>
          </header>

          {/* 项目概述 */}
          <section
            ref={(el) => (sectionRefs.current[0] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.overview[language] || translations.overview.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed">
              {translations.description[language] ||
                translations.description.en}
            </p>

            {/* 项目展示图 */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
              <div className="p-2">
                <img
                  src="/assets/projects/blackDeathSimulation/main.gif"
                  alt="Black Death SIR Model Simulation"
                  className="w-full rounded-lg"
                />
                <div className="text-center text-sm text-neutral-500 mt-2">
                  {language === "en"
                    ? "SIR model-based visualization of Black Death spread across medieval Europe"
                    : language === "zh"
                    ? "基于SIR模型的中世纪欧洲黑死病传播可视化"
                    : "SIRモデルに基づく中世ヨーロッパにおけるペスト感染拡大の視覚化"}
                </div>
              </div>
            </div>

            {/* 课程信息 */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <h3 className="text-lg font-medium mb-4 text-neutral-800">
                {translations.courseInfo[language] ||
                  translations.courseInfo.en}
              </h3>
              <p className="text-neutral-700 mb-2">
                <span className="font-medium">
                  {translations.course[language] || translations.course.en}
                </span>{" "}
                CSCI-UA MATH-UA 144 330 2024 Spring 001 (Undergrad)
              </p>
              <p className="text-neutral-700 mb-2">
                <span className="font-medium">
                  {translations.instructor[language] ||
                    translations.instructor.en}
                </span>{" "}
                Prof. Charles S. Peskin
              </p>
            </div>
          </section>

          {/* 方法论 */}
          <section
            ref={(el) => (sectionRefs.current[1] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.methodology[language] ||
                translations.methodology.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed">
              {translations.methodologyText[language] ||
                translations.methodologyText.en}
            </p>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="md:flex">
                <div className="md:w-1/2 p-6 flex flex-col justify-center">
                  <h3 className="text-xl font-medium mb-3 text-neutral-800">
                    {translations.results[language] || translations.results.en}
                  </h3>
                  <p className="text-neutral-600">
                    {translations.resultsText[language] ||
                      translations.resultsText.en}
                  </p>
                </div>
                <div className="md:w-1/2 bg-gray-50 p-4 flex items-center justify-center">
                  <img
                    src="/assets/projects/blackDeathSimulation/cover.jpg"
                    alt="Historical trade routes and plague spread"
                    className="max-w-full rounded-lg"
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlackDeathSimulationPage;
